import { useEffect, useId, useMemo, useRef, useState, type FocusEvent as ReactFocusEvent } from "react";
import PhoneInputLib, {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
  type Country,
  type Value,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./PhoneInput.css";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

interface CountrySelectOption {
  value?: Country;
  label: string;
  divider?: boolean;
}

interface CountrySelectProps {
  disabled?: boolean;
  readOnly?: boolean;
  value?: Country;
  onChange: (country?: Country) => void;
  options?: CountrySelectOption[];
  iconComponent?: (props: { country: Country; label: string }) => JSX.Element;
  name?: string;
  className?: string;
  onFocus?: (event: ReactFocusEvent<HTMLElement>) => void;
  onBlur?: (event: ReactFocusEvent<HTMLElement>) => void;
}

function detectCountryFromE164(phoneValue: string): Country | undefined {
  if (!phoneValue || !isValidPhoneNumber(phoneValue)) {
    return undefined;
  }

  try {
    return parsePhoneNumber(phoneValue)?.country;
  } catch {
    return undefined;
  }
}

function CountrySelect({
  disabled,
  readOnly,
  value,
  onChange,
  options = [],
  iconComponent: Icon,
  className,
  onFocus,
  onBlur,
}: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const countryOptions = useMemo(
    () =>
      options.filter((option): option is CountrySelectOption & { value: Country } => {
        if (!option.value || option.divider) {
          return false;
        }

        return /^[A-Z]{2}$/.test(option.value);
      }),
    [options]
  );

  const selectedOption = useMemo(
    () => countryOptions.find((option) => option.value === value),
    [countryOptions, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return countryOptions;
    }

    return countryOptions.filter((option) => {
      const dialCode = `+${getCountryCallingCode(option.value)}`;
      const searchable = `${option.label} ${option.value} ${dialCode}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [countryOptions, search]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const isDisabled = Boolean(disabled || readOnly);
  const selectedDialCode = selectedOption ? `+${getCountryCallingCode(selectedOption.value)}` : "";

  return (
    <div className={`phone-input-country-select ${className ?? ""}`} ref={rootRef}>
      <button
        type="button"
        className="phone-input-country-select__trigger"
        onClick={() => {
          if (!isDisabled) {
            setIsOpen((open) => !open);
          }
        }}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <span className="phone-input-country-select__value">
          {selectedOption && Icon ? (
            <Icon country={selectedOption.value} label={selectedOption.label} />
          ) : (
            <span className="phone-input-country-select__flag-fallback">🌐</span>
          )}
          <span className="phone-input-country-select__label">
            {selectedOption ? `${selectedOption.label} (${selectedDialCode})` : "Select country"}
          </span>
        </span>
        <span aria-hidden="true" className="phone-input-country-select__chevron">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="phone-input-country-select__dropdown" role="dialog" aria-label="Country selection">
          <input
            ref={searchInputRef}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search country or dial code"
            className="phone-input-country-select__search"
          />

          <ul className="phone-input-country-select__list" role="listbox">
            {filteredOptions.map((option) => {
              const dialCode = `+${getCountryCallingCode(option.value)}`;
              const isSelected = option.value === value;

              return (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`phone-input-country-select__option ${isSelected ? "is-selected" : ""}`}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="phone-input-country-select__option-main">
                      {Icon ? (
                        <Icon country={option.value} label={option.label} />
                      ) : (
                        <span className="phone-input-country-select__flag-fallback">🏳️</span>
                      )}
                      <span>{option.label}</span>
                    </span>
                    <span className="phone-input-country-select__dial-code">{dialCode}</span>
                  </button>
                </li>
              );
            })}

            {filteredOptions.length === 0 && (
              <li className="phone-input-country-select__empty">No countries found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function PhoneInput({ value, onChange, label, placeholder, className }: PhoneInputProps) {
  const inputId = useId();
  const [defaultCountry, setDefaultCountry] = useState<Country | undefined>(() => detectCountryFromE164(value));

  useEffect(() => {
    const detectedCountry = detectCountryFromE164(value);
    if (detectedCountry) {
      setDefaultCountry(detectedCountry);
    }
  }, [value]);

  const rootClassName = ["phone-input", className].filter(Boolean).join(" ");

  return (
    <div className={rootClassName}>
      {label && (
        <label className="phone-input__label" htmlFor={inputId}>
          {label}
        </label>
      )}

      <PhoneInputLib
        id={inputId}
        className="phone-input__control"
        value={value || undefined}
        onChange={(nextValue: Value | undefined) => onChange(nextValue ?? "")}
        defaultCountry={defaultCountry}
        international={false}
        countryCallingCodeEditable={false}
        placeholder={placeholder}
        countrySelectComponent={CountrySelect}
      />
    </div>
  );
}
